import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';
import { invoke } from '@tauri-apps/api/core';
import { Plus, MoreHorizontal, Plug, Unplug, Pencil, Trash2, Lock, Server, Search, X, Users, Key, Clock } from 'lucide-react';
import OsScroll from './ui/OsScroll';
import { isTauri } from '../utils/tauri';
import { subscribeTauriEvent } from '../utils/safeTauriEvent';
import { useFtpStore } from '../store/ftpStore';
import { openVaultGate, vaultErrorToken } from '../store/vaultStore';
import { useConnectionRows } from '../hooks/useConnectionRows';
import useConnectionProjectGroups from '../scope/useConnectionProjectGroups';
import { KIND_SSH } from '../scope/connectionProjectGroups';
import ConnectionGroups from './projectScope/ConnectionGroups';
import { projectMenuItems } from './projectScope/connectionProjectMenu';
import { refreshTeamVault } from '../store/teamVaultStore';
import teamVaultService, { teamVaultErrorMessage } from '../services/teamVaultService';
import {
  connectionMatchesQuery,
  connectionRowActions,
  connectionSummary,
  isTeamRow,
} from '../utils/ftpConnectionRows';
import VaultItemModal, { buildData } from './VaultItemModal';
import Tooltip from './ui/Tooltip';
import './Ftp.css';
import LocalCapHint from './plans/LocalCapHint';

// Same row recipe as the extension sidebar tree (ExtensionTreeNode) and the
// Collections column — the locked reminder below renders as one of those
// rows, not as a bespoke note, so the FTP and DB Manager sidebars say "Vault
// locked" identically. Local copy by the same convention Sidebar.js and
// SchemaSidebar.js already follow.
const SIDEBAR_TREE_ROW_IDLE =
  'border border-transparent hover:bg-gray-50 hover:border-gray-200';

/**
 * FTP sidebar: the vault-backed connection list. Each row is a vault `sftp`
 * envelope — name, protocol badge, user@host:port from the plaintext index,
 * and a status dot. Clicking a disconnected row IS the connect gesture
 * (DB-Manager convention — never auto-connect on panel activation). The "…"
 * menu (portaled, splitter stacking-context reason) offers Connect/Disconnect,
 * Edit (VaultItemModal on the decrypted item) and Remove — which deletes the
 * VAULT item itself, since the connection and the vault record are one.
 *
 * The titlebar's "+" action (ExtensionTitleBarActions) dispatches the
 * 'ftp-new-connection' CustomEvent handled here; the footer button is the
 * in-pane equivalent. Both open VaultItemModal preset to the sftp type.
 */

/** Portaled "…" dropdown anchored under a row button (VaultView pattern). */
function RowMenu({ anchorEl, items, onClose }) {
  const menuRef = useRef(null);
  const [pos, setPos] = useState(null);

  useEffect(() => {
    const rect = anchorEl?.getBoundingClientRect();
    if (rect) setPos({ left: Math.max(8, rect.right - 176), top: rect.bottom + 4 });
  }, [anchorEl]);

  useEffect(() => {
    const onMouseDown = (e) => {
      if (menuRef.current?.contains(e.target) || anchorEl?.contains(e.target)) return;
      onClose();
    };
    const onKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [anchorEl, onClose]);

  if (!pos) return null;
  return ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="fixed z-50 min-w-40 animate-fadeIn rounded-lg border border-gray-200 bg-white py-2 shadow-lg dark:border-zinc-700 dark:bg-zinc-900"
      style={{ left: pos.left, top: pos.top }}
      onContextMenu={(e) => e.preventDefault()}
    >
      {items.map(({ id, label, Icon, danger, onSelect }) => (
        <button
          key={id}
          type="button"
          className={`ctx-menu-btn${danger ? ' ctx-menu-btn--danger' : ''}`}
          onClick={() => {
            onClose();
            onSelect();
          }}
        >
          <Icon size={14} />
          <span>{label}</span>
        </button>
      ))}
    </div>,
    document.body
  );
}

export default function FtpSidebar() {
  const { t } = useTranslation();
  const connState = useFtpStore((s) => s.connState);
  const activeConnId = useFtpStore((s) => s.activeConnId);
  const vaultLocked = useFtpStore((s) => s.vaultLocked);

  const [menu, setMenu] = useState(null); // { id, anchorEl }
  const [confirmRemoveId, setConfirmRemoveId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  // { teamId, itemId, version } while editing a TEAM row — routes the save to
  // the team API instead of vault_put_item. Null for personal rows.
  const [editTeamContext, setEditTeamContext] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  /**
   * Personal + team SFTP connections, merged FOR DISPLAY ONLY, and the
   * destinations a new one may be saved to.
   *
   * Shared with the FTP view and the Terminal's SSH picker via
   * `useConnectionRows` — see that hook for what is deliberately NOT done
   * (`ftpStore.connections` is left exactly as it was; nothing derived from
   * `vault_list` ever gains a team row).
   */
  const { rows, destinations, byTeam: teamVaults } = useConnectionRows();

  // Load the vault-backed list on mount — envelopes only, no auto-connect.
  useEffect(() => {
    if (isTauri()) useFtpStore.getState().loadConnections();
  }, []);

  // Rust emits vault:changed after every successful vault mutation — keeps
  // this list in sync with edits made in the Vault panel (or the bridge).
  useEffect(() => {
    if (!isTauri()) return undefined;
    const sub = subscribeTauriEvent('vault:changed', () => {
      useFtpStore.getState().loadConnections();
    });
    return () => sub.unlisten();
  }, []);

  const openCreate = async () => {
    projectGroups.expectNew(); // a connection saved now joins the active project
    setEditItem(null);
    setEditTeamContext(null);
    // Saving needs an unlocked vault; unlock is auto-initializing + cheap.
    if (isTauri()) {
      try {
        await invoke('vault_unlock');
        useFtpStore.getState().loadConnections();
      } catch (e) {
        // `passphrase_required` is a Protected vault asking for the passphrase.
        // Opening the create modal anyway would only fail later at save time
        // with `vault_locked`, so ask for the passphrase instead.
        // `vault_needs_bootstrap` (never-bootstrapped scope) and
        // `vault_kdf_corrupt` (unopenable-with-any-secret kdf block) are the
        // same request-for-the-gate shape (vault-storage-transition phase 7).
        if (
          vaultErrorToken(e) === 'passphrase_required' ||
          vaultErrorToken(e) === 'vault_needs_bootstrap' ||
          vaultErrorToken(e) === 'vault_kdf_corrupt'
        ) {
          openVaultGate('ftp');
          return;
        }
        console.warn('[ftp] vault unlock failed:', e?.message || e);
      }
    }
    setModalOpen(true);
  };

  // Titlebar "+" action (ExtensionTitleBarActions 'ftp.panel' header).
  useEffect(() => {
    const onNew = () => {
      openCreate();
    };
    window.addEventListener('ftp-new-connection', onNew);
    return () => window.removeEventListener('ftp-new-connection', onNew);
    // openCreate is stable enough for this listener (no reactive deps).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Open the edit form for a row.
   *
   * The two vaults are fetched by two different routes and that is the whole
   * point — a team id must never reach `vault_get_item`, which looks in the
   * personal table. `isTeamRow` decides; `row.id` for a team row is
   * `team:<teamId>:<itemId>` and could not resolve there anyway.
   *
   * `canWrite` is re-checked here even though `connectionRowActions` already
   * withheld the menu entry: the menu is a UI affordance and this is the
   * function it calls. Both guards are cheap and neither is the real access
   * check — the server is.
   */
  const openEdit = async (row) => {
    if (!row?.canWrite) return;
    // A temporary connection has no stored item to re-open. Removing and
    // remaking it is the honest affordance; `connectionRowActions` withholds
    // Edit for these rows, and this is the guard for the call it would make.
    if (row.temporary) return;
    try {
      if (isTeamRow(row)) {
        // The rate-limited, audited body read. Called at the moment of use,
        // never to populate the list.
        const full = await teamVaultService.getItem(row.teamId, row.itemId);
        setEditItem({ id: row.id, type: full.item_type, name: full.name, data: full.data });
        setEditTeamContext({
          teamId: row.teamId,
          itemId: row.itemId,
          version: full.version,
          folderId: full.folder_id ?? null,
        });
        setModalOpen(true);
        return;
      }
      const full = await invoke('vault_get_item', { id: row.id });
      setEditItem(full);
      setEditTeamContext(null);
      setModalOpen(true);
    } catch (e) {
      console.warn('[ftp] load connection for edit failed:', teamVaultErrorMessage(e, e?.message));
    }
  };

  const handleRowClick = (row) => {
    const st = connState[row.id] || 'disconnected';
    useFtpStore.getState().setActiveConnId(row.id);
    // Clicking a disconnected (or lost) connection IS the connect gesture.
    // The row travels with the id so `connect` knows which vault to resolve the
    // credential from without having to guess from the id's shape.
    if (st === 'disconnected' || st === 'error' || st === 'lost') {
      useFtpStore.getState().connect(row.id, row);
    }
  };

  const removeConnection = async (row) => {
    setConfirmRemoveId(null);
    if (!row?.canWrite) return;
    const st = connState[row.id];
    if (st === 'connected' || st === 'connecting') {
      await useFtpStore.getState().disconnect(row.id);
    }
    try {
      // A temporary connection was never written anywhere, so removing it is
      // dropping it from memory. Sending this id to `vault_delete_item` would
      // tombstone nothing and log a failure for a row that is doing exactly
      // what it promised.
      if (row.temporary) {
        useFtpStore.getState().removeTempConnection(row.id);
        return;
      }
      if (isTeamRow(row)) {
        // Deleting a TEAM credential removes it for everyone with access, so it
        // goes to the team API — never to vault_delete_item, which would look
        // for the id in the personal table and tombstone nothing.
        await teamVaultService.deleteItem(row.teamId, row.itemId);
        await refreshTeamVault(row.teamId);
        return;
      }
      // The connection IS the vault item — deleting it tombstones the record;
      // vault:changed then refreshes both this list and the Vault panel.
      await invoke('vault_delete_item', { id: row.id });
      useFtpStore.getState().loadConnections();
    } catch (e) {
      console.warn('[ftp] remove connection failed:', teamVaultErrorMessage(e, e?.message));
    }
  };

  /**
   * The row's "…" menu.
   *
   * Which actions exist at all is decided by `connectionRowActions`, which
   * withholds Edit and Remove entirely from a read-only team row. Not disabled
   * entries, and not entries that open a form and fail at save: filling in a
   * whole credential form before being refused is the bad experience, and
   * Remove on a shared production credential is not something to discover at
   * the API boundary.
   */
  const menuItemsFor = (row) => {
    const st = connState[row.id] || 'disconnected';
    const byId = {
      disconnect: {
        id: 'disconnect',
        label: t('ftp.disconnect'),
        Icon: Unplug,
        onSelect: () => useFtpStore.getState().disconnect(row.id),
      },
      connect: {
        id: 'connect',
        label: t('ftp.connect'),
        Icon: Plug,
        onSelect: () => {
          useFtpStore.getState().setActiveConnId(row.id);
          useFtpStore.getState().connect(row.id, row);
        },
      },
      edit: { id: 'edit', label: t('common.edit'), Icon: Pencil, onSelect: () => openEdit(row) },
      remove: {
        id: 'remove',
        label: t('ftp.remove'),
        Icon: Trash2,
        danger: true,
        onSelect: () => setConfirmRemoveId(row.id),
      },
    };
    return [...connectionRowActions(row, st).map((id) => byId[id]).filter(Boolean), ...projectMenuItems(projectGroups.membershipAction(row), t)];
  };

  // Name, protocol, and user@host:port (what the row displays) are all
  // searchable — the index holds them in plaintext, for team rows too.
  const visibleConnections = rows.filter((row) => connectionMatchesQuery(row, searchTerm));
  const projectGroups = useConnectionProjectGroups(visibleConnections, { kind: KIND_SSH, listId: 'ftp' });

  return (
    <div className="ftp-sidebar">
      {/* Fixed filter header — mirrors ExtensionSidebarSections/Collections */}
      <div className="flex-shrink-0 px-2 pt-2 border-b border-gray-200">
        <div className="sidebar-panel-search-row">
          <Search size={14} className="text-gray-400 flex-shrink-0" aria-hidden />
          <input
            type="text"
            placeholder={t('ftp.filterConnections', 'Filter connections')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="tab-dropdown-search-input min-w-0 flex-1 bg-transparent text-gray-900 placeholder-gray-400 border-none p-0 focus:outline-none"
            autoComplete="off"
          />
          <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center">
            {searchTerm ? (
              <button
                type="button"
                onClick={() => setSearchTerm('')}
                className="flex h-full w-full items-center justify-center rounded bg-transparent p-0 cursor-pointer text-gray-900 hover:opacity-70 focus:outline-none"
                aria-label={t('sidebar.clearSearch', 'Clear filter')}
                onMouseDown={(e) => e.preventDefault()}
              >
                <X size={14} strokeWidth={2} aria-hidden />
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {/* Above the list, not in a footer — the position the DB Manager's has
          (ExtensionSidebarSections renders its manifest `plus` action in exactly
          this slot, between the fixed filter header and the scrolling tree).
          The two sidebars sit one tab apart and the button was in two different
          places (owner, 23.09.2026). */}
      <div className="ftp-new-conn-row">
        <button type="button" className="ftp-new-conn-btn" onClick={openCreate}>
          <Plus size={13} /> {t('ftp.newConnection')}
        </button>
        <LocalCapHint kind="hosts" style={{ marginLeft: 6 }} />
      </div>

      <OsScroll style={{ flex: 1, minHeight: 0 }}>
        <div className="ftp-conn-list">
          {/* The locked note ADDS to the list rather than replacing it: rows
              that survive a lock are exactly the live sessions (the store
              filters to them), and hiding a running session's row made it
              unreachable — the lock gates new dials, not open ones. */}
          {vaultLocked ? (
            // Styled and worded exactly like the DB Manager's locked tree row
            // (label, icon size, row hover, click-to-unlock) — the two
            // sidebars sit one tab apart and used to answer the same state
            // with two different designs.
            <div
              role="button"
              tabIndex={0}
              onClick={() => useFtpStore.getState().unlockVault()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  useFtpStore.getState().unlockVault();
                }
              }}
              // `paddingLeft: 4` is ExtensionTreeNode's inline `4 + depth * 14`
              // at depth 0 — the DB Manager's locked row is a tree LEAF, and
              // this row has to be the same one.
              style={{ paddingLeft: 4 }}
              className={`flex items-center px-2 py-1 rounded-md group cursor-pointer transition-colors duration-150 ${SIDEBAR_TREE_ROW_IDLE}`}
            >
              {/* The leaf's 18px spacer, so the lock sits where the DB row's
                  does rather than 20px to its left. */}
              <span className="ftp-row-lead" aria-hidden />
              <Lock size={12} className="text-gray-500 dark:text-zinc-400 shrink-0 mr-2" aria-hidden />
              <span className="flex-1 min-w-0 text-xs font-medium text-gray-800 dark:text-zinc-200 truncate">
                {t('vault.unlockTitle', 'Vault locked')}
              </span>
              {/* The same hover "…" the DB row gets from its tree actions —
                  ExtensionTreeNode's button classes, opening the sidebar's own
                  RowMenu with the one action the DB menu offers. */}
              <button
                type="button"
                className="opacity-0 group-hover:opacity-100 shrink-0 p-1 bg-transparent hover:bg-gray-200 dark:hover:bg-zinc-700 rounded transition-colors duration-150 cursor-pointer ml-1"
                title={t('ftp.moreActions')}
                aria-label={t('ftp.moreActions')}
                onClick={(e) => {
                  e.stopPropagation();
                  setMenu({ id: '__vault_locked__', anchorEl: e.currentTarget });
                }}
              >
                <MoreHorizontal size={12} className="text-gray-500 dark:text-zinc-400" />
              </button>
            </div>
          ) : null}
          {vaultLocked && visibleConnections.length === 0 ? null : !vaultLocked &&
            rows.length === 0 ? (
            <div className="ftp-sidebar-note">
              <Server size={13} />
              <span>{t('ftp.noConnections')}</span>
            </div>
          ) : visibleConnections.length === 0 ? (
            <div className="ftp-sidebar-note">
              <Search size={13} />
              <span>{t('ftp.noMatches', 'No connections match your filter.')}</span>
            </div>
          ) : (
            // Project scope (card 5.2): the project's connections on top.
            <ConnectionGroups groups={projectGroups} renderRow={(row) => {
              const st = connState[row.id] || 'disconnected';
              const proto = String(row.index?.protocol || 'sftp').toUpperCase();
              return (
                <div
                  key={row.id}
                  className={`ftp-conn-row group${
                    row.id === activeConnId ? ' ftp-conn-row--active' : ''
                  }`}
                  onClick={() => handleRowClick(row)}
                >
                  {/* The two slots a DB tree row leads with: the caret gutter
                      (empty — nothing here expands) and the icon. The status
                      dot is this row's icon, so labels land on the same pixel
                      in both panels. */}
                  <span className="ftp-row-lead" aria-hidden />
                  <span className="ftp-row-icon">
                    <span className={`ftp-status-dot ftp-status-dot--${st}`} />
                  </span>
                  <div className="ftp-conn-texts">
                    <div className="ftp-conn-title-line">
                      <span className="ftp-conn-name">{row.name}</span>
                      <span className="ftp-proto-badge">{proto}</span>
                      {/* Origin tag. A user looking at a production host has to
                          be able to tell whether it is theirs alone or shared —
                          the two have different privacy properties (the team
                          vault is server-readable) and deleting one of them
                          affects other people. */}
                      {/* Says out loud that this one disappears. The same
                          reason the team tag exists: a row's lifetime and its
                          privacy are not guessable from its name, and finding
                          out by restarting is the bad way to learn it. */}
                      {row.temporary && (
                        <Tooltip content={t('ftp.temporaryHint')} side="bottom-start">
                          <span className="ftp-temp-badge">
                            <Clock size={10} aria-hidden />
                            {t('ftp.temporaryBadge')}
                          </span>
                        </Tooltip>
                      )}
                      {isTeamRow(row) && (
                        <span
                          className="ftp-team-badge"
                          title={t('ftp.teamConnectionTitle', { team: row.teamName })}
                        >
                          <Users size={10} aria-hidden />
                          {row.teamName}
                          {!row.canWrite ? ` · ${t('ftp.readOnly')}` : ''}
                        </span>
                      )}
                    </div>
                    {confirmRemoveId === row.id ? (
                      <div className="ftp-conn-confirm" onClick={(e) => e.stopPropagation()}>
                        <span>
                          {isTeamRow(row) ? t('ftp.removeConfirmTeam') : t('ftp.removeConfirm')}
                        </span>
                        <button
                          type="button"
                          className="ftp-mini-btn ftp-mini-btn--danger"
                          onClick={() => removeConnection(row)}
                        >
                          {t('ftp.remove')}
                        </button>
                        <button
                          type="button"
                          className="ftp-mini-btn"
                          onClick={() => setConfirmRemoveId(null)}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    ) : (
                      <span className="ftp-conn-sub">{connectionSummary(row)}</span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="ftp-conn-menu-btn opacity-0 group-hover:opacity-100"
                    title={t('ftp.moreActions')}
                    aria-label={t('ftp.moreActions')}
                    onClick={(e) => {
                      e.stopPropagation();
                      setMenu({ id: row.id, anchorEl: e.currentTarget });
                    }}
                  >
                    <MoreHorizontal size={13} />
                  </button>
                </div>
              );
            }} />
          )}
        </div>
      </OsScroll>

      {menu &&
        (() => {
          // The locked reminder is not a connection row — its menu is the one
          // action the DB Manager's locked row offers, same wording and icon.
          if (menu.id === '__vault_locked__') {
            return (
              <RowMenu
                anchorEl={menu.anchorEl}
                items={[
                  {
                    id: 'unlock-vault',
                    label: t('ftp.unlockVaultAction', 'Unlock vault'),
                    Icon: Key,
                    onSelect: () => useFtpStore.getState().unlockVault(),
                  },
                ]}
                onClose={() => setMenu(null)}
              />
            );
          }
          const row = rows.find((r) => r.id === menu.id);
          if (!row) return null;
          return (
            <RowMenu
              anchorEl={menu.anchorEl}
              items={menuItemsFor(row)}
              onClose={() => setMenu(null)}
            />
          );
        })()}

      <VaultItemModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditTeamContext(null);
        }}
        item={editItem}
        initialType="sftp"
        destinations={destinations}
        byTeam={teamVaults}
        teamContext={editTeamContext}
        /* Dial something once without committing it to your vault — the DB
           Manager's ad-hoc connection, in the place FTP already asks where a
           connection should be saved. The body is held for the session and
           never written; see ftpStore's `tempBodies`. */
        ephemeralOption={{
          label: t('ftp.dontSave', 'Don’t save (this session only)'),
          note: t(
            'ftp.dontSaveNote',
            'Kept until you close Apiboo. Nothing is written to your vault.'
          ),
          onSave: ({ name, values }) => {
            const id = useFtpStore
              .getState()
              .addTempConnection({ name, data: buildData('sftp', values) });
            useFtpStore.getState().setActiveConnId(id);
          },
        }}
      />
    </div>
  );
}
